import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import BookingForm from "@/components/BookingForm";
import { Wordmark, LogoMark } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import TrackingPill from "@/components/TrackingPill";
import { CheckCircle2, Copy, PackageSearch, MapPin, ShieldCheck, Zap, Globe } from "lucide-react";
import { toast } from "@/lib/toast";

const PERKS = [
  { icon: Zap, t: "Instant tracking number" },
  { icon: Globe, t: "Auto-routed to the right country portal" },
  { icon: ShieldCheck, t: "Branded AWB & documents" },
];

export default function GuestBooking() {
  const [result, setResult] = useState(null);
  const trackUrl = result ? `${window.location.origin}/track?tn=${result.tracking_number}` : "";

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(trackUrl); toast.success("Tracking link copied"); }
    catch { toast.error("Could not copy"); }
  };

  return (
    <div className="relative min-h-screen overflow-hidden">
      <div className="pointer-events-none absolute -top-40 left-1/4 h-80 w-80 rounded-full bg-primary/15 blur-3xl animate-blob" />
      <div className="pointer-events-none absolute top-20 right-0 h-72 w-72 rounded-full bg-cyan-400/15 blur-3xl animate-blob" style={{ animationDelay: "4s" }} />
      <header className="glass sticky top-0 z-30 border-b border-border/80">
        <div className="h-0.5 w-full bg-gradient-to-r from-primary via-cyan-500 to-brand-red" />
        <div className="mx-auto flex h-16 max-w-3xl items-center justify-between px-4 sm:px-6">
          <Link to="/login" className="flex items-center gap-2"><LogoMark className="h-8 w-8" /><Wordmark className="text-xl" /></Link>
          <Link to="/track" className="inline-flex items-center gap-1.5 rounded-full border border-primary/20 bg-primary/5 px-3 py-1.5 text-sm font-semibold text-primary transition-colors hover:bg-primary/10" data-testid="guest-track-link"><MapPin className="h-4 w-4" /> Track a shipment</Link>
        </div>
      </header>

      <main className="relative mx-auto max-w-3xl px-4 py-8 sm:px-6 sm:py-10">
        {!result ? (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} data-testid="guest-booking-page">
            <div className="mb-1 text-[11px] font-mono-label font-semibold uppercase tracking-[0.3em] text-primary">Guest Booking</div>
            <h1 className="mb-1 font-display text-3xl font-extrabold tracking-tight sm:text-4xl">Book a Shipment</h1>
            <p className="mb-5 text-sm text-muted-foreground">No account needed — get a tracking number instantly.</p>
            <div className="mb-5 grid gap-2 sm:grid-cols-3">
              {PERKS.map((p, i) => (
                <motion.div key={p.t} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 + i * 0.07 }} className="flex items-center gap-2 rounded-xl border border-border bg-white/70 px-3 py-2 text-xs font-medium">
                  <span className="flex h-6 w-6 items-center justify-center rounded-lg bg-primary/10 text-primary"><p.icon className="h-3.5 w-3.5" /></span>{p.t}
                </motion.div>
              ))}
            </div>
            <div className="mb-6 flex flex-wrap items-center gap-2 rounded-2xl border border-border bg-secondary/40 px-4 py-3" data-testid="guest-account-options">
              <span className="text-sm text-muted-foreground">Prefer to keep all your shipments in one place? <span className="font-medium text-foreground">(optional)</span></span>
              <div className="ml-auto flex gap-2">
                <Link to="/register"><Button size="sm" variant="outline" data-testid="guest-signup-button">Sign up</Button></Link>
                <Link to="/login"><Button size="sm" variant="ghost" data-testid="guest-login-button">Sign in</Button></Link>
              </div>
            </div>
            <div className="panel p-5 sm:p-7">
              <BookingForm guest onCreated={setResult} />
            </div>
          </motion.div>
        ) : (
          <motion.div initial={{ opacity: 0, scale: 0.96, y: 12 }} animate={{ opacity: 1, scale: 1, y: 0 }} transition={{ type: "spring", stiffness: 260, damping: 24 }}
            className="panel relative mx-auto max-w-lg overflow-hidden p-8 text-center" data-testid="guest-booking-success">
            <div className="pointer-events-none absolute -top-20 left-1/2 h-48 w-80 -translate-x-1/2 rounded-full bg-emerald-400/15 blur-3xl" />
            <div className="relative mb-3 flex justify-center">
              <span className="pop-in relative flex h-16 w-16 items-center justify-center rounded-full bg-emerald-500/10 text-emerald-500 ring-1 ring-emerald-500/30"><CheckCircle2 className="h-9 w-9" /></span>
            </div>
            <h1 className="relative font-display text-2xl font-extrabold tracking-tight">Booking Confirmed!</h1>
            <p className="relative mt-1 text-sm text-muted-foreground">Your shipment has been booked{result.source_portal ? ` via ${result.source_portal}` : ""}.</p>
            <div className="relative mt-5 rounded-2xl border border-border bg-slate-50/70 p-4">
              <div className="text-[11px] font-mono-label font-semibold uppercase tracking-widest text-muted-foreground">Tracking Number</div>
              <div className="mt-1 font-display text-3xl font-extrabold tracking-tight text-primary" data-testid="guest-tracking-number">{result.tracking_number}</div>
              {result.awb_number && <div className="mt-2 flex justify-center"><TrackingPill tn={result.awb_number} /></div>}
            </div>
            <div className="relative mt-4 flex flex-col justify-center gap-2 sm:flex-row">
              <Button variant="outline" onClick={copyLink} data-testid="guest-copy-link-button"><Copy className="h-4 w-4" /> Copy Tracking Link</Button>
              <Link to={`/track?tn=${result.tracking_number}`}><Button className="w-full shine-hover" data-testid="guest-track-now-button"><PackageSearch className="h-4 w-4" /> Track Now</Button></Link>
            </div>
            <div className="relative mt-4">
              <button onClick={() => setResult(null)} className="text-sm text-muted-foreground transition-colors hover:text-primary" data-testid="guest-book-another">Book another shipment</button>
            </div>
          </motion.div>
        )}
      </main>
    </div>
  );
}
